import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for browser-based React apps (merchant & customer).
 *
 * Call this ONCE at app startup, before React renders.
 * The DSN is read from the VITE_SENTRY_DSN env variable.
 * If the variable is missing, Sentry is silently disabled.
 *
 * @param appName  "merchant" | "customer" – used as the Sentry `environment` tag
 *                 and to differentiate issues between the two apps.
 */
export function initSentry(appName: 'merchant' | 'customer'): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (!dsn) {
    console.info('[Sentry] No VITE_SENTRY_DSN found – Sentry is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // "development" | "production"
    release: `paulownia-${appName}@${__APP_VERSION__}`,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance monitoring – sample 10% of transactions in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay – capture 1% normally, 100% on errors
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,

    // Default tags
    initialScope: {
      tags: {
        app: appName,
      },
    },

    // Don't send events in development by default (override with env var)
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',

    // Filter noisy errors
    beforeSend(event) {
      // Ignore network errors that are expected in offline-first PWAs
      const message = event.exception?.values?.[0]?.value ?? '';
      if (
        message.includes('Failed to fetch') ||
        message.includes('NetworkError') ||
        message.includes('Load failed')
      ) {
        return null;
      }
      return event;
    },
  });

  // Tag the app name for filtering in the Sentry dashboard
  Sentry.setTag('app', appName);
}

/**
 * Set the current user context for Sentry.
 * Call after login / tenant selection so errors are linked to users.
 */
export function setSentryUser(user: { id: string; email?: string; tenantId?: string }): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
  if (user.tenantId) {
    Sentry.setTag('tenantId', user.tenantId);
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

// Re-export Sentry for direct use if needed (e.g. ErrorBoundary wrapper)
export { Sentry };
